import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { CaseTagSelector } from "../cases/CaseTagSelector";
import { updateProject } from "../../../api/project";
import { useProjectPermissions } from "../../../routes/roleOnly";

export function ProjectStatusTagChips({ tags = [], className = "" }) {
  if (!Array.isArray(tags) || tags.length === 0) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${className}`.trim()}>
      {tags.map((tag) => (
        <span
          key={tag.id}
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
          style={
            tag.color
              ? { backgroundColor: `${tag.color}30`, color: tag.color }
              : { backgroundColor: "#f1f5f9", color: "#334155" }
          }
        >
          {tag.name}
        </span>
      ))}
    </div>
  );
}

export async function persistProjectTagIds(projectId, tagIds) {
  const response = await updateProject({ id: projectId, tag_ids: tagIds });
  if (!response?.id) {
    throw new Error("tag save failed");
  }
  return response;
}

export function ProjectStatusTagsControl({
  project,
  onSaved,
  definitions = null,
  buttonLabel = null,
}) {
  const { t } = useTranslation();
  const permissions = useProjectPermissions(project);
  const canEdit = !!permissions?.canEdit;
  const [saving, setSaving] = useState(false);
  const selectedIds = (project?.tag_definitions || []).map((tag) => tag.id);

  const handleChange = useCallback(
    async (tagIds) => {
      if (!project?.id || !canEdit) return;
      setSaving(true);
      try {
        const updated = await persistProjectTagIds(project.id, tagIds);
        onSaved?.(updated);
      } catch (_err) {
        toast.error(t("project.status_tags.save_error"));
      } finally {
        setSaving(false);
      }
    },
    [project?.id, canEdit, onSaved, t]
  );

  return (
    <div className={saving ? "opacity-70" : undefined}>
      <CaseTagSelector
        value={selectedIds}
        onChange={canEdit ? handleChange : undefined}
        readOnly={!canEdit}
        showBadges
        scopeModule="project"
        definitions={definitions}
        buttonLabel={buttonLabel || t("project.status_tags.title")}
      />
    </div>
  );
}
